import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { brandRoutes } from "~/ui-rtk/constants/routes/brand-routes"
import {
  useGetExperimentsQuery,
  useUpdateExperimentMutation,
  useRequestDesignMutation,
  useStartExperimentMutation,
  useConfirmEndExperimentMutation,
  useLazyGetDesignArtifactsQuery,
} from "~/ui-rtk/api/experimentApi"
import { Loader } from "~/ui-rtk/components/ui/common"
import Alert from "~/ui-rtk/components/ui/common/Alert"

import { Button } from "~/ui-rtk/components/ui/controls"
import Table, {
  type TTableHeaderProp,
  type TCellItem,
} from "~/ui-rtk/components/ui/common/Table"
import { H2, P } from "~/ui-rtk/components/ui/typography"
import useDrawer from "~/ui-rtk/hooks/drawer"
import { DRAWER_TYPE } from "~/ui-rtk/constants/drawer"
import useDate from "~/ui-rtk/hooks/date"
import ExperimentUploadButton from "./components/ExperimentUploadButton"
import DesignPowerSummary from "./components/DesignPowerSummary"

import type {
  ExperimentRecord,
  ExperimentStatus,
  ExperimentDesignStatus,
} from "~/ui-rtk/types/experiments"

const statusColorMap: Record<ExperimentStatus, string> = {
  draft: "bg-basic-blue/50 text-system-blue-200",
  scheduled: "bg-warning/30 text-warning",
  needs_confirmation: "bg-system-error-900/40 text-system-error-300",
  live: "bg-system-green-900/40 text-system-green-300",
  completed: "bg-basic-grey-inactive/20 text-basic-grey-inactive",
}

const designStatusColorMap: Record<ExperimentDesignStatus, string> = {
  pending: "bg-warning/30 text-warning",
  running: "bg-warning/30 text-warning",
  ready: "bg-system-green-900/40 text-system-green-300",
  failed: "bg-system-error-900/40 text-system-error-300",
}

const designStatusLabelMap: Record<ExperimentDesignStatus, string> = {
  pending: "design pending",
  running: "design running",
  ready: "design ready",
  failed: "design failed",
}

/**
 * Nothing flips SCHEDULED→LIVE server-side when the start date arrives —
 * liveness is computed from the committed start date at read time.
 */
const displayStatus = (experiment: ExperimentRecord): ExperimentStatus => {
  if (
    experiment.status === "scheduled" &&
    experiment.isConfirmed &&
    experiment.startDate &&
    new Date(experiment.startDate).getTime() <= Date.now()
  ) {
    return "live"
  }
  return experiment.status
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { data?: { message?: string } })?.data?.message ?? fallback

export default function ExperimentsPage() {
  const { openDrawer } = useDrawer()
  const dayjs = useDate()

  const { data: experiments = [], isLoading, error } = useGetExperimentsQuery()
  const [updateExperiment] = useUpdateExperimentMutation()
  const [requestDesign] = useRequestDesignMutation()
  const [startExperiment] = useStartExperimentMutation()
  const [confirmEndExperiment] = useConfirmEndExperimentMutation()
  const [getDesignArtifacts] = useLazyGetDesignArtifactsQuery()

  const handleLegacyConfirm = async (experimentId: string) => {
    await updateExperiment({
      id: experimentId,
      body: {
        isConfirmed: true,
      },
    })
  }

  const handleRequestDesign = async (experimentId: string) => {
    try {
      await requestDesign(experimentId).unwrap()
      toast.success(
        "Design requested — the status flips to ready when the pipeline finishes",
      )
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to request the design"))
    }
  }

  const handleStart = async (experimentId: string) => {
    try {
      await startExperiment(experimentId).unwrap()
      toast.success("Experiment started — analysis runs daily while live")
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to start the experiment"))
    }
  }

  const handleConfirmEnd = async (experimentId: string) => {
    try {
      await confirmEndExperiment({ id: experimentId }).unwrap()
      toast.success(
        "Experiment ended — final results settle over the next month",
      )
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to end the experiment"))
    }
  }

  const handleDownloadDesign = async (experimentId: string) => {
    try {
      const { url } = await getDesignArtifacts(experimentId).unwrap()
      window.open(url, "_blank", "noopener")
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to fetch the design download"))
    }
  }

  const navigate = useNavigate()

  const handleViewResults = (experiment: ExperimentRecord) => {
    // Results are keyed by the pipeline identifier when the experiment has
    // been through a design run; fall back to the display name.
    const resultsName = experiment.experimentName ?? experiment.name
    navigate(
      `${brandRoutes.EXPERIMENTS.RESULTS}?name=${encodeURIComponent(resultsName)}`,
    )
  }

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return <Alert variant="danger" content="Failed to load experiments" />
  }

  const getStatusMessage = (experiment: ExperimentRecord): string | null => {
    if (experiment.designStatus === "failed") {
      return "Design failed — retry, or contact support if it keeps failing."
    }
    if (experiment.status === "draft" && !experiment.designStatus) {
      return "Request a design to begin"
    }
    if (experiment.status === "needs_confirmation") {
      return "Test has started. Please confirm the start date."
    }
    return null
  }

  const headers: TTableHeaderProp[] = [
    {
      label: "Experiment",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        const shownStatus = displayStatus(experiment)
        const statusStyles = statusColorMap[shownStatus]
        const statusMessage = getStatusMessage(experiment)

        return (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-white">
              {experiment.name}
            </span>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={`rounded-full px-2 py-1 font-semibold uppercase ${statusStyles}`}
                >
                  {shownStatus.replace("_", " ")}
                </span>
                {experiment.designStatus && (
                  <span
                    className={`rounded-full px-2 py-1 font-semibold uppercase ${designStatusColorMap[experiment.designStatus]}`}
                  >
                    {designStatusLabelMap[experiment.designStatus]}
                  </span>
                )}
              </div>
              {statusMessage && (
                <span className="text-xs text-system-error-300">
                  {statusMessage}
                </span>
              )}
              {experiment.designStatus === "ready" && (
                <DesignPowerSummary experiment={experiment} />
              )}
            </div>
          </div>
        )
      },
    },
    {
      label: "Start Date",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        const date = experiment.startDate || experiment.estimatedStartDate

        if (!date) {
          return <span className="text-basic-grey-inactive">Not set</span>
        }

        return (
          <div className="flex flex-col gap-1">
            <span className="text-white">
              {dayjs(date).format("MMM D, YYYY")}
            </span>
            {!experiment.startDate && (
              <span className="text-xs text-basic-grey-inactive">
                Estimated
              </span>
            )}
          </div>
        )
      },
    },
    {
      label: "End Date",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        const date = experiment.endDate || experiment.estimatedEndDate

        if (!date) {
          return <span className="text-basic-grey-inactive">Not set</span>
        }

        return (
          <div className="flex flex-col gap-1">
            <span className="text-white">
              {dayjs(date).format("MMM D, YYYY")}
            </span>
            {!experiment.endDate && (
              <span className="text-xs text-basic-grey-inactive">
                Estimated
              </span>
            )}
          </div>
        )
      },
    },
    {
      label: "Duration",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        return (
          <div className="flex flex-col gap-1">
            <span className="text-white">{experiment.durationDays} days</span>
            <span className="text-xs text-basic-grey-inactive">
              {experiment.cadence} grain
            </span>
          </div>
        )
      },
      align: "center",
    },
    {
      label: "Control",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        return (
          <span className="text-white">
            {experiment.testPercentage}% test · {experiment.controlPercentage}%
            control
          </span>
        )
      },
      align: "center",
    },
    {
      label: "Actions",
      customRender: (item: TCellItem) => {
        const experiment = item as ExperimentRecord
        const shownStatus = displayStatus(experiment)
        const isPreLaunch =
          !experiment.isConfirmed &&
          (experiment.status === "draft" || experiment.status === "scheduled")
        const designInFlight =
          experiment.designStatus === "pending" ||
          experiment.designStatus === "running"
        const canRequestDesign =
          isPreLaunch && !designInFlight && experiment.designStatus !== "ready"
        const canStart = isPreLaunch && experiment.designStatus === "ready"
        const isRunning =
          shownStatus === "live" ||
          (experiment.status === "scheduled" && experiment.isConfirmed)
        const needsLegacyConfirm = experiment.status === "needs_confirmation"

        return (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isPreLaunch && !experiment.designStatus && (
              <ExperimentUploadButton
                experimentId={experiment.id}
                kind="HISTORICAL"
                label="Upload historicals"
              />
            )}
            {canRequestDesign && (
              <Button
                onClick={() => handleRequestDesign(experiment.id)}
                className="p-2 px-4 text-sm"
              >
                <p className="text-sm">
                  {experiment.designStatus === "failed"
                    ? "Retry design"
                    : "Request design"}
                </p>
              </Button>
            )}
            {experiment.hasDesignArtifacts && (
              <Button
                onClick={() => handleDownloadDesign(experiment.id)}
                className="p-2 px-4 text-sm bg-system-blue-600 text-white"
              >
                <p className="text-sm">Download design</p>
              </Button>
            )}
            {canStart && (
              <Button
                onClick={() => handleStart(experiment.id)}
                className="p-2 px-4 text-sm"
              >
                <p className="text-sm">Start</p>
              </Button>
            )}
            {needsLegacyConfirm && (
              <Button
                onClick={() => handleLegacyConfirm(experiment.id)}
                className="p-2 px-4 text-sm"
              >
                <p className="text-sm">Confirm</p>
              </Button>
            )}
            {isRunning && (
              <>
                <ExperimentUploadButton
                  experimentId={experiment.id}
                  kind="RESULTS"
                  label="Upload results"
                />
                <Button
                  onClick={() => handleConfirmEnd(experiment.id)}
                  className="p-2 px-4 text-sm"
                >
                  <p className="text-sm">Confirm end</p>
                </Button>
              </>
            )}
            <Button
              onClick={() => handleViewResults(experiment)}
              className="p-2 px-4 text-sm"
            >
              <p className="text-sm">Results</p>
            </Button>
          </div>
        )
      },
      align: "right",
    },
  ]

  const handleOpenDrawer = () => {
    openDrawer({
      type: DRAWER_TYPE.ExperimentsForm,
      clickSourceId: "experiments-create-button",
      props: {},
    })
  }

  return (
    <div className="flex h-full flex-col gap-6 bg-basic-black px-4 py-4 text-basic-light">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="grid gap-1">
          <H2 className="text-white">Experiments</H2>
          <P className="text-sm text-basic-grey-inactive">
            Geo-holdout incrementality tests. Configure a test, request its
            design, download targeting lists, and launch. Results are analyzed
            daily while the experiment is live.
          </P>
        </div>
        <Button
          id="experiments-create-button"
          onClick={handleOpenDrawer}
          className="w-fit  p-2 px-4"
        >
          <p className="text-sm">New experiment</p>
        </Button>
      </header>

      <section className="">
        <div className="flex flex-col gap-6">
          <Table headers={headers} items={experiments} />
        </div>
      </section>
    </div>
  )
}
